import { ResponseWrapper, PagedResults, trackerGet, trackerPost, trackerPatch, trackerDelete, decodeResult } from "./basicApi.ts";
import { ValidatorStep } from "../validation.ts";
import { Uid, ObjectWithUid } from "../utils.tsx";


enum EditAsChoice {
  Inline = "inline",
  Pulldown = "pulldown",
  Textbox = "textbox",
  JSON = "json",
}


interface ParameterDto extends ObjectWithUid {
  slug: string;
  name: string;
  description: string;
  slug_beamline: string;
  // Universal ordering of this parameter in the UI relative to the others for the Beamline
  _order: number;
  id_derived_from?: Uid;
  // If no default is provided the default is assumed to be "".
  standard_default?: string;
  // If the value should be unique across all samples in the same set.
  enforce_unique_in_set: boolean;
  // When new instances are generated, and the value should be unique, use this interval when auto-generating new values.
  auto_generate_interval?: number;
  // How this parameter should be edited in the UI.
  edit_as: EditAsChoice;
  // If a choices field is present, we provide a pulldown instead of a text field and restrict the choices to this array.
  choices?: { name: string, description: string }[] | null;
  validation?: ValidatorStep | null;
  schema_identifier?: string;
  date_last_modified: string;
}


interface ParameterCreateDto {
  slug?: string;
  name: string;
  description?: string;
  slug_beamline: string;
  // Universal ordering of this parameter in the UI relative to the others for the Beamline
  id_derived_from?: Uid;
  // If no default is provided the default is assumed to be "".
  standard_default?: string;
  // If the value should be unique across all samples in the same set.
  enforce_unique_in_set?: boolean;
  // When new instances are generated, and the value should be unique, use this interval when auto-generating new values.
  auto_generate_interval?: number;
  // How this parameter should be edited in the UI. Defaults to "string" if not provided.
  edit_as?: EditAsChoice;
  // If a choices field is present, we provide a pulldown instead of a text field and restrict the choices to this array.
  choices?: { name: string, description: string }[];
  validation?: ValidatorStep;
  schema_identifier?: string;
}


interface ParameterUpdateDto extends ObjectWithUid {
  slug?: string;
  name?: string;
  description?: string;
  // Universal ordering of this parameter in the UI relative to the others for the Beamline
  id_derived_from?: Uid | null;
  // If no default is provided the default is assumed to be "".
  standard_default?: string | null;
  // If the value should be unique across all samples in the same set.
  enforce_unique_in_set: boolean;
  // When new instances are generated, and the value should be unique, use this interval when auto-generating new values.
  auto_generate_interval?: number | null;
  // How this parameter should be edited in the UI.
  edit_as?: EditAsChoice;
  // If a choices field is present, we provide a pulldown instead of a text field and restrict the choices to this array.
  choices?: { name: string, description: string }[] | null;
  validation?: ValidatorStep | null;
  schema_identifier?: string;
}


// Get a list of Parameters the logged-in user has access to
async function getParameters(): Promise<ResponseWrapper<PagedResults<ParameterDto>>> {
  const result = await trackerGet(`parameters/`, {});
  return decodeResult(result);
}


// Create a Parameter record on the server
async function createParameter(dto: ParameterCreateDto): Promise<ResponseWrapper<ParameterDto>> {
  const result = await trackerPost(`parameters/`, JSON.stringify(dto));
  return decodeResult(result);
}


// Update a Parameter record on the server.
async function updateParameter(dto: ParameterUpdateDto): Promise<ResponseWrapper<ParameterDto>> {
  const result = await trackerPatch(`parameters/${dto.id}/`, JSON.stringify(dto));
  return decodeResult(result);
}


// Delete a Parameter record from the server, returning the Id used if successful.
async function deleteParameter(id: Uid): Promise<ResponseWrapper<Uid>> {
  const result = await trackerDelete(`parameters/${id}/`, {});
 if (result.success) {
   return { success: true, response: id };
 }
 return { success: false, message: result.message };
}


export type { ParameterDto, ParameterCreateDto, ParameterUpdateDto };
export { EditAsChoice, getParameters, createParameter, updateParameter, deleteParameter }
